/**
 * Leitura de um ZIP direto do `Blob`, por fatias.
 *
 * A alternativa pronta (`JSZip.loadAsync`) carrega o arquivo inteiro para a memória antes de
 * entregar a primeira entrada: num aparelho de 2GB, um zip de imagens de algumas dezenas de MB
 * vira o dobro disso em heap e o navegador passa a coletar lixo no meio do trabalho — é aí que o
 * processo deixa de ser lento e passa a ser insuportável.
 *
 * Aqui nada além dos cabeçalhos é lido. O diretório central diz onde cada arquivo começa, e o
 * conteúdo sai como `blob.slice(inicio, fim)` — que **não copia bytes**: é uma referência à mesma
 * região do arquivo que o IndexedDB já mantém em disco. Para entradas guardadas sem compressão
 * (`STORE`), que é o caso comum de imagem — webp/png/jpeg já vêm comprimidos e o zip não ganha
 * nada tentando de novo —, extrair uma imagem custa zero CPU e zero memória.
 *
 * O que não é suportado devolve `null` em vez de tentar adivinhar: ZIP64, diretório central
 * inconsistente, método de compressão exótico. Quem chama cai no leitor completo.
 */

const ASSINATURA_EOCD = 0x06054b50;
const ASSINATURA_CENTRAL = 0x02014b50;
const ASSINATURA_LOCAL = 0x04034b50;

const TAMANHO_EOCD = 22;
const TAMANHO_CENTRAL_FIXO = 46;
const TAMANHO_LOCAL_FIXO = 30;

/** Comentário do zip: o EOCD pode estar até esse tanto antes do fim do arquivo. */
const MAX_COMENTARIO = 0xffff;

const METODO_STORE = 0;
const METODO_DEFLATE = 8;

/** Campo estourado no cabeçalho clássico: o valor real está num registro ZIP64. */
const SENTINELA_16 = 0xffff;
const SENTINELA_32 = 0xffffffff;

export type EntradaZip = {
  nome: string;
  metodo: number;
  tamanhoComprimido: number;
  tamanhoOriginal: number;
  /** Onde o cabeçalho local do arquivo começa dentro do zip. */
  offsetLocal: number;
  /** Pasta: entra no diretório como um registro vazio, sem conteúdo para extrair. */
  ehDiretorio: boolean;
};

const MIME_POR_EXTENSAO: Record<string, string> = {
  webp: 'image/webp',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

export const mimeDoArquivo = (nome: string): string => {
  const extensao = nome.split('.').pop()?.toLowerCase() ?? '';
  return MIME_POR_EXTENSAO[extensao] ?? '';
};

const lerFatia = async (blob: Blob, inicio: number, fim: number): Promise<DataView> =>
  new DataView(await blob.slice(inicio, fim).arrayBuffer());

type FluxoLegivel = {
  getReader: () => { read: () => Promise<{ done: boolean; value?: Uint8Array }> };
};

/**
 * Junta os pedaços de um fluxo. Sem `Response`: o mesmo código roda na aba, no worker e no teste,
 * e a única coisa que se pede do ambiente é o `ReadableStream`.
 */
const consumirFluxo = async (fluxo: FluxoLegivel): Promise<Uint8Array[]> => {
  const leitor = fluxo.getReader();
  const pedacos: Uint8Array[] = [];

  for (;;) {
    const { done, value } = await leitor.read();
    if (done) break;
    if (value) pedacos.push(value);
  }

  return pedacos;
};

/**
 * Entradas do diretório central, ou `null` se o arquivo não for um zip clássico legível.
 *
 * Lê dois pedaços: a cauda (onde mora o EOCD) e o diretório central. Num zip de 10 mil imagens o
 * diretório tem algumas centenas de KB — o conteúdo em si não é tocado.
 */
export const lerDiretorioZip = async (zip: Blob): Promise<EntradaZip[] | null> => {
  try {
    if (!zip || zip.size < TAMANHO_EOCD) return null;

    const tamanhoCauda = Math.min(zip.size, TAMANHO_EOCD + MAX_COMENTARIO);
    const cauda = await lerFatia(zip, zip.size - tamanhoCauda, zip.size);

    // O EOCD é o último registro, mas pode ter um comentário depois: procura-se de trás para a
    // frente, que é onde ele está em todo zip sem comentário — o caso normal, achado de primeira.
    let posEocd = -1;
    for (let i = cauda.byteLength - TAMANHO_EOCD; i >= 0; i -= 1) {
      if (cauda.getUint32(i, true) === ASSINATURA_EOCD) {
        posEocd = i;
        break;
      }
    }
    if (posEocd < 0) return null;

    const totalEntradas = cauda.getUint16(posEocd + 10, true);
    const tamanhoCentral = cauda.getUint32(posEocd + 12, true);
    const offsetCentral = cauda.getUint32(posEocd + 16, true);

    if (totalEntradas === SENTINELA_16 || tamanhoCentral === SENTINELA_32 || offsetCentral === SENTINELA_32) return null;
    if (!totalEntradas || offsetCentral + tamanhoCentral > zip.size) return null;

    const central = await lerFatia(zip, offsetCentral, offsetCentral + tamanhoCentral);
    const decoder = new TextDecoder();
    const entradas: EntradaZip[] = [];

    let pos = 0;
    for (let i = 0; i < totalEntradas; i += 1) {
      if (pos + TAMANHO_CENTRAL_FIXO > central.byteLength) return null;
      if (central.getUint32(pos, true) !== ASSINATURA_CENTRAL) return null;

      const metodo = central.getUint16(pos + 10, true);
      const tamanhoComprimido = central.getUint32(pos + 20, true);
      const tamanhoOriginal = central.getUint32(pos + 24, true);
      const tamNome = central.getUint16(pos + 28, true);
      const tamExtra = central.getUint16(pos + 30, true);
      const tamComentario = central.getUint16(pos + 32, true);
      const offsetLocal = central.getUint32(pos + 42, true);

      if (tamanhoComprimido === SENTINELA_32 || offsetLocal === SENTINELA_32) return null;

      const inicioNome = pos + TAMANHO_CENTRAL_FIXO;
      if (inicioNome + tamNome > central.byteLength) return null;

      const nome = decoder.decode(new Uint8Array(central.buffer, central.byteOffset + inicioNome, tamNome));
      entradas.push({ nome, metodo, tamanhoComprimido, tamanhoOriginal, offsetLocal, ehDiretorio: nome.endsWith('/') });

      pos = inicioNome + tamNome + tamExtra + tamComentario;
    }

    return entradas;
  } catch {
    return null;
  }
};

/** `true` se o navegador sabe inflar sem biblioteca — o que dispensa carregar o zip inteiro. */
const temDescompressaoNativa = (): boolean =>
  typeof (globalThis as { DecompressionStream?: unknown }).DecompressionStream === 'function';

/** Bloco lido de uma vez. Grande o bastante para caber muitas imagens numa leitura só. */
const TAMANHO_JANELA_PADRAO = 4 * 1024 * 1024;

const juntarPedacos = (pedacos: Uint8Array[]): Uint8Array => {
  if (pedacos.length === 1) return pedacos[0];

  const total = pedacos.reduce((soma, pedaco) => soma + pedaco.length, 0);
  const saida = new Uint8Array(total);
  let pos = 0;
  for (const pedaco of pedacos) {
    saida.set(pedaco, pos);
    pos += pedaco.length;
  }
  return saida;
};

const inflarBytes = async (bytes: Uint8Array): Promise<Uint8Array | null> => {
  if (!temDescompressaoNativa()) return null;

  try {
    const contexto = globalThis as unknown as { DecompressionStream: new (formato: string) => unknown };
    const origem = new Blob([bytes as unknown as BlobPart]);
    const fluxo = origem.stream() as unknown as { pipeThrough: (destino: unknown) => FluxoLegivel };

    const inflado = fluxo.pipeThrough(new contexto.DecompressionStream('deflate-raw'));
    return juntarPedacos(await consumirFluxo(inflado));
  } catch {
    return null;
  }
};

/**
 * Percorre as entradas lendo o zip em blocos grandes, na ordem em que estão no arquivo.
 *
 * É esta função que deve ser usada para extrair muitos arquivos — e não `extrairArquivoZip` num
 * laço. Cada `blob.slice(...).arrayBuffer()` é uma viagem assíncrona ao subsistema de Blob do
 * navegador, com leitura em disco no fim; num aparelho de flash lento, milhares dessas viagens
 * em posições espalhadas custam mais do que ler o arquivo inteiro de uma vez. Aqui o número de
 * leituras é `tamanho do zip / tamanho da janela` — algumas dezenas —, todas sequenciais, e o
 * cabeçalho local de cada entrada já vem dentro do bloco, sem leitura extra.
 *
 * O conteúdo entregue é uma **cópia** dos bytes da janela — e bytes crus, não `Blob`: quem grava
 * no IndexedDB quer justamente evitar o `Blob`, que o navegador materializa como um arquivo
 * próprio em disco. O bloco pode ser descartado assim que a janela avança, e nenhuma imagem
 * segura os 4MB atrás dela.
 *
 * Devolve `false` na primeira entrada que não der para extrair (cabeçalho inconsistente, método
 * de compressão que o navegador não infla sozinho) — quem chama refaz o zip com o leitor completo.
 */
export const percorrerArquivosZip = async (
  zip: Blob,
  entradas: EntradaZip[],
  aoExtrair: (entrada: EntradaZip, conteudo: Uint8Array) => Promise<void> | void,
  opcoes: { filtro?: (entrada: EntradaZip) => boolean; tamanhoJanela?: number } = {}
): Promise<boolean> => {
  const { filtro, tamanhoJanela = TAMANHO_JANELA_PADRAO } = opcoes;

  // Ordem física, não a do diretório: é o que torna a leitura sequencial.
  const alvos = entradas
    .filter((entrada) => !entrada.ehDiretorio && (!filtro || filtro(entrada)))
    .sort((a, b) => a.offsetLocal - b.offsetLocal);

  let janelaInicio = 0;
  let janela: Uint8Array | null = null;

  const carregarJanela = async (inicio: number, minimo: number) => {
    const fim = Math.min(zip.size, inicio + Math.max(tamanhoJanela, minimo));
    janela = new Uint8Array(await zip.slice(inicio, fim).arrayBuffer());
    janelaInicio = inicio;
  };

  const dentroDaJanela = (inicio: number, fim: number) =>
    janela != null && inicio >= janelaInicio && fim <= janelaInicio + janela.length;

  try {
    for (const entrada of alvos) {
      // Só o cabeçalho local por enquanto: o tamanho do nome e do extra dele é que dizem onde os
      // dados começam, e podem diferir do que está no diretório central.
      if (!dentroDaJanela(entrada.offsetLocal, entrada.offsetLocal + TAMANHO_LOCAL_FIXO)) {
        await carregarJanela(entrada.offsetLocal, TAMANHO_LOCAL_FIXO + entrada.tamanhoComprimido);
      }

      let rel = entrada.offsetLocal - janelaInicio;
      let cabecalho = new DataView(janela!.buffer, janela!.byteOffset + rel, TAMANHO_LOCAL_FIXO);
      if (cabecalho.getUint32(0, true) !== ASSINATURA_LOCAL) return false;

      const tamNome = cabecalho.getUint16(26, true);
      const tamExtra = cabecalho.getUint16(28, true);
      const deslocamentoDados = TAMANHO_LOCAL_FIXO + tamNome + tamExtra;
      const inicioDados = entrada.offsetLocal + deslocamentoDados;
      const fimDados = inicioDados + entrada.tamanhoComprimido;
      if (fimDados > zip.size) return false;

      // Entrada maior que a janela, ou que atravessa o fim dela: recarrega a partir dela mesma.
      if (!dentroDaJanela(inicioDados, fimDados)) {
        await carregarJanela(entrada.offsetLocal, deslocamentoDados + entrada.tamanhoComprimido);
        rel = entrada.offsetLocal - janelaInicio;
        cabecalho = new DataView(janela!.buffer, janela!.byteOffset + rel, TAMANHO_LOCAL_FIXO);
        if (cabecalho.getUint32(0, true) !== ASSINATURA_LOCAL) return false;
      }

      const inicioNaJanela = inicioDados - janelaInicio;
      const bytes = janela!.subarray(inicioNaJanela, inicioNaJanela + entrada.tamanhoComprimido);

      let conteudo: Uint8Array | null;
      if (entrada.metodo === METODO_STORE) {
        // `slice` copia — de propósito: `subarray` seguraria a janela inteira de 4MB atrás de
        // cada imagem, e o bloco precisa poder ser descartado quando a janela avança.
        conteudo = bytes.slice();
      } else if (entrada.metodo === METODO_DEFLATE) {
        conteudo = await inflarBytes(bytes);
      } else {
        conteudo = null;
      }

      if (!conteudo) return false;
      await aoExtrair(entrada, conteudo);
    }

    return true;
  } catch {
    return false;
  } finally {
    janela = null;
  }
};

/**
 * Conteúdo de uma entrada, ou `null` quando não dá para extrair sem o leitor completo.
 *
 * Para muitos arquivos use `percorrerArquivosZip`: cada chamada aqui custa duas viagens ao disco.
 *
 * O tamanho do nome e do extra vem do cabeçalho **local**, não do central: os dois podem diferir,
 * e é o local que diz onde os bytes começam de verdade.
 */
export const extrairArquivoZip = async (zip: Blob, entrada: EntradaZip): Promise<Uint8Array | null> => {
  try {
    const cabecalho = await lerFatia(zip, entrada.offsetLocal, entrada.offsetLocal + TAMANHO_LOCAL_FIXO);
    if (cabecalho.byteLength < TAMANHO_LOCAL_FIXO) return null;
    if (cabecalho.getUint32(0, true) !== ASSINATURA_LOCAL) return null;

    const tamNome = cabecalho.getUint16(26, true);
    const tamExtra = cabecalho.getUint16(28, true);

    const inicio = entrada.offsetLocal + TAMANHO_LOCAL_FIXO + tamNome + tamExtra;
    const fim = inicio + entrada.tamanhoComprimido;
    if (fim > zip.size) return null;

    const bytes = new Uint8Array(await zip.slice(inicio, fim).arrayBuffer());

    if (entrada.metodo === METODO_STORE) return bytes;
    if (entrada.metodo !== METODO_DEFLATE) return null;

    return inflarBytes(bytes);
  } catch {
    return null;
  }
};
